import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import PrinterSummaryCard from './PrinterSummaryCard';

const PrinterSelector = ({ selectedPrinter, onPrinterChange, printers, onNext }) => {
  const [hoveredPrinter, setHoveredPrinter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const getStatusLabelPt = (status) => {
    switch (status) {
      case 'active':
        return 'Operacional';
      case 'idle':
        return 'Ociosa';
      case 'maintenance':
        return 'Em manutenção';
      case 'inactive':
        return 'Fora de serviço';
      default:
        return 'Desconhecido';
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Build filter options based on current printer list
  const brandOptions = useMemo(() => {
    const set = new Set((printers || []).map(p => (p?.brand || p?.manufacturer || '')).filter(Boolean));
    return [{ value: '', label: 'Todas as marcas' }, ...Array.from(set).sort().map(b => ({ value: b, label: b }))];
  }, [printers]);

  const modelOptions = useMemo(() => {
    const set = new Set((printers || []).map(p => (p?.model || '')).filter(Boolean));
    return [{ value: '', label: 'Todos os modelos' }, ...Array.from(set).sort().map(m => ({ value: m, label: m }))];
  }, [printers]);

  const statusOptions = [
    { value: 'all', label: 'Todos os status' },
    { value: 'active', label: 'Operacional' },
    { value: 'idle', label: 'Ociosa' },
    { value: 'maintenance', label: 'Em manutenção' },
    { value: 'inactive', label: 'Fora de serviço' },
  ];

  const sortOptions = [
    { value: 'name', label: 'Nome (A→Z)' },
    { value: 'name-desc', label: 'Nome (Z→A)' },
    { value: 'hourly-rate', label: 'Taxa/h (↓)' },
    { value: 'power', label: 'Potência (↓)' },
    { value: 'speed', label: 'Velocidade (↓)' },
  ];

  const filteredPrinters = useMemo(() => {
    const term = searchTerm?.toLowerCase()?.trim();

    let list = (printers || []).filter(p => {
      // Search by name or model
      const nameMatch = (p?.name || '')?.toLowerCase()?.includes(term || '');
      const modelMatch = (p?.model || '')?.toLowerCase()?.includes(term || '');
      const brandMatch = (p?.brand || p?.manufacturer || '')?.toLowerCase()?.includes(term || '');
      const matchesSearch = !term || nameMatch || modelMatch || brandMatch;

      // Status filter
      const matchesStatus = statusFilter === 'all' || (p?.status === statusFilter);

      // Brand filter
      const matchesBrand = !brandFilter || ((p?.brand || p?.manufacturer || '') === brandFilter);

      // Model filter
      const matchesModel = !modelFilter || ((p?.model || '') === modelFilter);

      return matchesSearch && matchesStatus && matchesBrand && matchesModel;
    });

    // Sorting
    list = list.sort((a, b) => {
      const aName = (a?.name || '').toLowerCase();
      const bName = (b?.name || '').toLowerCase();
      const aRate = Number(a?.hourlyRate ?? a?.hourly_rate ?? 0);
      const bRate = Number(b?.hourlyRate ?? b?.hourly_rate ?? 0);
      const aPower = Number(a?.powerConsumption ?? a?.power_consumption ?? 0);
      const bPower = Number(b?.powerConsumption ?? b?.power_consumption ?? 0);
      const aSpeed = Number(a?.max_speed_mm_s ?? 0);
      const bSpeed = Number(b?.max_speed_mm_s ?? 0);

      switch (sortBy) {
        case 'name-desc':
          return bName.localeCompare(aName);
        case 'hourly-rate':
          return bRate - aRate;
        case 'power':
          return bPower - aPower;
        case 'speed':
          return bSpeed - aSpeed;
        case 'name':
        default:
          return aName.localeCompare(bName);
      }
    });

    return list;
  }, [printers, searchTerm, statusFilter, brandFilter, modelFilter, sortBy]);

  // Participação de uso relativa ao conjunto mostrado
  const totalHoursAll = useMemo(() => {
    const list = Array.isArray(filteredPrinters) ? filteredPrinters : printers;
    const eligible = list?.filter(p => p?.status !== 'nonexistent') || [];
    return eligible?.reduce((sum, p) => sum + (Number(p?.totalHours || 0)), 0) || 0;
  }, [filteredPrinters, printers]);

  const usageShareByPrinter = useMemo(() => {
    const list = Array.isArray(filteredPrinters) ? filteredPrinters : printers;
    const eligible = list?.filter(p => p?.status !== 'nonexistent') || [];
    const denominator = totalHoursAll > 0 ? totalHoursAll : eligible?.length > 0 ? eligible?.reduce((sum, p) => sum + (Number(p?.utilization || 0)), 0) : 0;
    const map = {};
    eligible?.forEach((p) => {
      const hours = Number(p?.totalHours || 0);
      const util = Number(p?.utilization || 0);
      const numerator = hours > 0 ? hours : util; // fallback simples
      map[p?.id] = denominator > 0 ? (numerator / denominator) * 100 : 0;
    });
    return map;
  }, [filteredPrinters, printers, totalHoursAll]);


  if (printers?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center animate-fade-in">
        <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <Icon name="Printer" size={40} className="text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-4">Nenhuma Impressora 3D Cadastrada</h3>
        <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
          Você precisa cadastrar pelo menos uma impressora 3D para usar a calculadora profissional.
        </p>
        <Button 
          onClick={() => window.location.href = '/printer-management'}
          className="flex items-center space-x-3 bg-gradient-to-r from-primary to-secondary px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl"
        >
          <Icon name="Plus" size={20} />
          <span>Cadastrar Primeira Impressora</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-8 animate-fade-in">
      <div className="flex items-center space-x-4 mb-8">
        <div className="w-14 h-14 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-xl flex items-center justify-center shadow-lg">
          <Icon name="Printer" size={28} className="text-primary" />
        </div>
        <div>
          <h3 className="text-3xl font-bold text-foreground">Selecionar Impressora 3D</h3>
          <p className="text-muted-foreground text-lg">Escolha a impressora que será utilizada neste projeto</p>
        </div>
      </div>
      {/* Controles de busca, filtros e ordenação */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[240px]">
          <Input
            label="Buscar por nome/modelo"
            placeholder="Digite o nome ou modelo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
        </div>
        <div className="w-[220px]">
          <Select
            label="Status"
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            searchable
            clearable
          />
        </div>
        <div className="w-[220px]">
          <Select
            label="Marca"
            options={brandOptions}
            value={brandFilter}
            onChange={setBrandFilter}
            searchable
            clearable
          />
        </div>
        <div className="w-[220px]">
          <Select
            label="Modelo"
            options={modelOptions}
            value={modelFilter}
            onChange={setModelFilter}
            searchable
            clearable
          />
        </div>
        <div className="w-[220px]">
          <Select
            label="Ordenar por"
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
          />
        </div>
      </div>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8"
      >
        {filteredPrinters?.map((printer) => {
          const isSelected = selectedPrinter === printer?.id;
          const usagePct = usageShareByPrinter?.[printer?.id] || 0;
          return (
            <motion.div key={printer?.id} variants={cardVariants} whileHover={{ y: -4 }} whileTap={{ scale: 0.98 }}>
              <PrinterSummaryCard
                printer={printer}
                selected={isSelected}
                usageSharePct={usagePct}
                onSelect={onPrinterChange}
              />
            </motion.div>
          );
        })}
      </motion.div>
      {selectedPrinter && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-primary/20 rounded-xl p-6 bg-gradient-to-br from-primary/5 to-secondary/5"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-success/20 rounded-full flex items-center justify-center shadow-lg">
                <Icon name="CheckCircle" size={28} className="text-success" />
              </div>
              <div>
                <p className="font-bold text-foreground text-xl">
                  {printers?.find(p => p?.id === selectedPrinter)?.name} selecionada
                </p>
                <p className="text-muted-foreground">
                  Agora vamos selecionar os filamentos para o projeto
                </p>
              </div>
            </div>
            <Button 
              onClick={onNext} 
              className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-primary to-secondary text-lg font-semibold shadow-lg hover:shadow-xl"
            >
              <span>Continuar</span>
              <Icon name="ArrowRight" size={20} />
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PrinterSelector;